import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { useSession } from "@/services/auth";
import { useState } from "react";
import { openPdfForPrint } from "@/lib/open-pdf-for-print";

/**
 * Phase 11a: customer dashboard's "all my pickup bookings" list + management
 * mutations (cancel, detach single shipment, update notes).
 *
 * Distinct from `use-attachable-pickup-bookings.ts`, which is the
 * address-scoped attachable filter for the create-order flow.
 */

const API_URL = import.meta.env.VITE_API_URL;

export type PickupBookingDerivedState =
  | "Scheduled"
  | "InProgress"
  | "Completed"
  | "Failed"
  | "Cancelled";

export interface MyPickupBookingShipment {
  id: string;
  shipmentNumber: number;
  trackingNumber: string | null;
  status: string;
  pickupPriceCents: number | null;
  canReschedulePickup: boolean;
  order: { id: string; orderNumber: number };
  tasks: { id: string; status: string }[];
}

export interface MyPickupBooking {
  contactSnapshotAt?: string | null;
  contactName?: string | null;
  contactCompany?: string | null;
  contactPhone?: string | null;
  contactEmail?: string | null;
  instructions?: string | null;
  isResidential?: boolean | null;
  id: string;
  scheduledDate: string;
  notes: string | null;
  /** Null = active; ISO timestamp = cancelled (and when). */
  cancelledAt: string | null;
  derivedState: PickupBookingDerivedState;
  attachedShipmentCount: number;
  totalShipmentCount: number;
  /** Phase 12 (#232): sum of |amountCents| across DISCOUNT-type
   *  PriceAdjustments scoped to this booking. Detail sheet displays as
   *  "Bundle discount: -$X.XX". 0 when no shipment was discounted. */
  bundleDiscountCents: number;
  /** Output tax on the displayed net (raw − bundle) at the booking's origin-
   *  province rate. Mirrors what was actually charged. 0 when no tax rate is
   *  configured for the province. */
  taxCents: number;
  pickupAddress: {
    id: string;
    line1: string;
    line2?: string | null;
    city: string;
    province: string;
    country: string;
    postalCode?: string | null;
  };
  slot: { id: string; name: string; startTime: string; endTime: string };
  dispatchCenter: {
    id: string;
    code: string;
    name: string;
    timeZone: string;
  };
  pickupService: { id: string; code: string; name: string };
  shipments: MyPickupBookingShipment[];
}

interface ListResponse {
  data: MyPickupBooking[];
}

const LIST_KEY = "myPickupBookings" as const;

export function useMyPickupBookings(args?: {
  state?: PickupBookingDerivedState;
}) {
  const { token, customer } = useSession();
  const state = args?.state;

  return useQuery<ListResponse>({
    queryKey: [LIST_KEY, customer?.id, state],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (state) params.state = state;
      const response = await axios.get<ListResponse>(
        `${API_URL}/api/v2/pickup-bookings`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!token && !!customer?.id,
    staleTime: 30_000,
  });
}

export function useMyPickupBooking(bookingId: string | null) {
  const { token, customer } = useSession();
  return useQuery<MyPickupBooking>({
    queryKey: [LIST_KEY, customer?.id, "detail", bookingId],
    queryFn: async () => {
      const { data } = await axios.get<{ data: MyPickupBooking }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: Boolean(token && customer?.id && bookingId),
  });
}

interface CancelArgs {
  bookingId: string;
  reason?: string;
}

/**
 * Phase 11c: customer-side cancel/detach create a PickupRefundRequest in
 * PENDING. Admin processes via the queue. Surface the request to the UI so
 * we can show the refund-pending message.
 */
export interface PickupRefundRequestSummary {
  id: string;
  kind: "DETACH" | "CANCEL" | "RESCHEDULE_REFUND";
  status: "PENDING" | "APPROVED" | "DENIED";
  expectedAmountCents: number;
}

export function useCancelPickupBooking() {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({ bookingId, reason }: CancelArgs) => {
      const { data } = await axios.post<{
        data: {
          booking: MyPickupBooking;
          refundRequest: PickupRefundRequestSummary | null;
        };
      }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/cancel`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [LIST_KEY, customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
    },
  });
}

interface DetachArgs {
  bookingId: string;
  shipmentId: string;
  reason?: string;
}

export function useDetachShipmentFromPickupBooking() {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({ bookingId, shipmentId, reason }: DetachArgs) => {
      const { data } = await axios.delete<{
        data: {
          booking: MyPickupBooking;
          refundRequest: PickupRefundRequestSummary | null;
        };
      }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/shipments/${shipmentId}`,
        {
          data: { reason },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [LIST_KEY, customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
    },
  });
}

interface UpdateNotesArgs {
  bookingId: string;
  notes: string | null;
}

export function useUpdatePickupBookingNotes() {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();

  return useMutation({
    mutationFn: async ({ bookingId, notes }: UpdateNotesArgs) => {
      const { data } = await axios.patch<{ data: MyPickupBooking }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [LIST_KEY, customer?.id],
      });
    },
  });
}

export function usePrintPickupBooking() {
  const { token } = useSession();
  const [isPending, setIsPending] = useState(false);

  const print = async (
    bookingId: string,
    messages: { loading: string; failed: string }
  ) => {
    if (isPending) return;
    setIsPending(true);
    try {
      await openPdfForPrint(async () => {
        if (!token || !API_URL || !bookingId)
          throw new Error("Pickup PDF unavailable");
        const response = await axios.get<Blob>(
          `${API_URL}/api/v2/pickup-bookings/${encodeURIComponent(bookingId)}/print`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: "blob",
          }
        );
        return response.data;
      }, messages);
    } finally {
      setIsPending(false);
    }
  };

  return { print, isPending };
}

/** Helper for handlers that want to surface 409 errors specifically. */
export function isConflictError(err: unknown): err is AxiosError<{
  error: string;
  message?: string;
}> {
  return (
    axios.isAxiosError(err) &&
    err.response?.status === 409 &&
    typeof err.response.data === "object"
  );
}

/**
 * Phase 11c slice 3: reschedule. Preview returns the delta + conflict
 * info; commit applies the booking move. v1 only supports same-price
 * reschedule (server rejects with PRICE_CHANGED otherwise).
 */
export interface ReschedulePreview {
  oldTotalCents: number;
  newTotalCents: number;
  deltaCents: number;
  perShipment: {
    id: string;
    shipmentNumber: number;
    oldPickupPriceCents: number;
    newPickupPriceCents: number;
  }[];
  deliveryOutcomes: {
    shipmentId: string;
    carrierServiceId: string;
    sourceRevision: number;
    pickupDate: string | null;
    deliveredFastest: string;
    deliveredSlowest: string;
    deliveredPrecision: "DATE" | "DATETIME";
    timeZone: string;
  }[];
  previewFingerprint: string;
  conflictingBookingId?: string;
}

interface RescheduleArgs {
  bookingId: string;
  newScheduledDate: string;
  newSlotId: string;
  newPickupServiceId?: string;
  shipmentIds: string[];
}

export function usePreviewReschedulePickupBooking() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (args: RescheduleArgs) => {
      const { bookingId, ...body } = args;
      const { data } = await axios.post<{ data: ReschedulePreview }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/reschedule/preview`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}

export function useReschedulePickupBooking() {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();
  return useMutation({
    mutationFn: async (
      args: RescheduleArgs & { expectedPreviewFingerprint: string }
    ) => {
      const { bookingId, ...body } = args;
      const { data } = await axios.post<{
        data: {
          booking: { id: string; scheduledDate: string; slotId: string };
        } & ReschedulePreview;
      }>(`${API_URL}/api/v2/pickup-bookings/${bookingId}/reschedule`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [LIST_KEY, customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
    },
  });
}

/** 422-with-error-code typed errors from the reschedule endpoints. */
export function isRescheduleStructuredError(err: unknown): err is AxiosError<{
  error: string;
  message?: string;
  details?: Record<string, unknown>;
}> {
  if (!axios.isAxiosError(err)) return false;
  const data: unknown = err.response?.data;
  return (
    typeof data === "object" &&
    data !== null &&
    "error" in data &&
    typeof (data as { error: unknown }).error === "string"
  );
}
